import React, { useEffect, useMemo, useState } from 'react';
import AppLayout from 'components/ui/AppLayout';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import OpportunitiesStats from './components/OpportunitiesStats';
import OpportunitiesTable from './components/OpportunitiesTable';
import { opportunitiesApi, pipelineApi } from 'api/deals';
import { reportsApi } from 'api/reports';
import { getCurrentUser } from 'api/client';
import { useToast } from 'components/ui/Toast';

const DEFAULT_STAGE_OPTIONS = [
  { value: 'all', label: 'All Stages' },
  { value: 1, label: 'Prospecting' },
  { value: 2, label: 'Qualification' },
  { value: 3, label: 'Proposal' },
  { value: 4, label: 'Negotiation' },
  { value: 5, label: 'Closed Won' },
  { value: 6, label: 'Closed Lost' },
];

export default function Opportunities() {
  const currentUser = getCurrentUser();
  const isManager = currentUser?.role === 'Owner' || currentUser?.role === 'Manager';
  const toast = useToast();
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState(null);
  const [stageConfig, setStageConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const stageOptions = useMemo(() => {
    const cfg = stageConfig?.stages || [];
    if (!cfg.length) return DEFAULT_STAGE_OPTIONS;
    return [
      { value: 'all', label: 'All Stages' },
      ...cfg.map((s) => ({ value: s.id, label: s.name })),
    ];
  }, [stageConfig]);

  const loadOpportunitiesData = async () => {
    const configResponse = await pipelineApi.getConfig();
    const cfg = configResponse?.stages || [];
    const wonStage = cfg.find((s) => s.isWon)?.id ?? 5;
    const lostStage = cfg.find((s) => s.isClosed && !s.isWon)?.id ?? 6;
    const [statsResponse, opportunitiesResponse] = await Promise.all([
      opportunitiesApi.getStats({ wonStage, lostStage }),
      opportunitiesApi.getAll(),
    ]);

    return {
      statsResponse,
      opportunitiesResponse: opportunitiesResponse || [],
      configResponse,
    };
  };

  const buildOpportunityPayload = (opportunity) => ({
    name: opportunity?.name?.trim(),
    accountName: opportunity?.company?.trim(),
    ownerName: opportunity?.rep || 'Unassigned',
    stage: Number(opportunity?.stage ?? 1),
    amount: Number(opportunity?.value || 0),
    probabilityPercent: Number(opportunity?.probability || 0),
    expectedCloseDateUtc: new Date(opportunity?.closeDate || Date.now()).toISOString(),
    description: opportunity?.source || null,
  });

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      setIsLoading(true);
      setLoadError('');
      try {
        const data = await loadOpportunitiesData();

        if (!isMounted) {
          return;
        }

        setStats(data.statsResponse);
        setOpportunities(data.opportunitiesResponse);
        setStageConfig(data.configResponse);
      } catch (error) {
        if (isMounted) {
          setLoadError(error?.message || 'Unable to load opportunities.');
          setStats(null);
          setOpportunities([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    load();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleOpportunitySubmit = async (formData) => {
    try {
      if (selectedOpportunity?.id) {
        await opportunitiesApi.update(selectedOpportunity.id, buildOpportunityPayload(formData));
        toast.success('Opportunity updated.');
      } else {
        await opportunitiesApi.create(buildOpportunityPayload(formData));
        toast.success('Opportunity created.');
      }

      const data = await loadOpportunitiesData();
      setStats(data.statsResponse);
      setOpportunities(data.opportunitiesResponse);
      setStageConfig(data.configResponse);
      setIsFormOpen(false);
      setSelectedOpportunity(null);
    } catch (error) {
      toast.error(error?.message || 'Could not save the opportunity.');
    }
  };

  const handleOpportunityDelete = async (opportunityId) => {
    try {
      await opportunitiesApi.remove(opportunityId);
      toast.success('Opportunity deleted.');
      const data = await loadOpportunitiesData();
      setStats(data.statsResponse);
      setOpportunities(data.opportunitiesResponse);
      setStageConfig(data.configResponse);
    } catch (error) {
      toast.error(error?.message || 'Could not delete the opportunity.');
    }
  };

  const statsCards = useMemo(() => {
    if (!stats) {
      return undefined;
    }

    const toCr = (value) => `₹${(value / 10000000).toFixed(1)}Cr`;
    const toL = (value) => `₹${(value / 100000).toFixed(1)}L`;

    return [
      { label: 'Total Opportunities', value: String(stats.totalOpportunities), change: null, icon: 'Target', color: '#1E40AF', bg: '#DBEAFE' },
      { label: 'Pipeline Value', value: toCr(stats.pipelineValue), change: null, icon: 'IndianRupee', color: '#7C3AED', bg: '#EDE9FE' },
      { label: 'Avg Deal Size', value: toL(stats.averageDealSize), change: null, icon: 'TrendingUp', color: '#059669', bg: '#DCFCE7' },
      { label: 'Win Rate', value: `${stats.winRatePercent}%`, change: null, icon: 'Award', color: '#D97706', bg: '#FEF3C7' },
    ];
  }, [stats]);

  return (
    <AppLayout>
      <div className="px-4 md:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1
              className="font-heading font-bold text-2xl md:text-3xl"
              style={{ color: 'var(--color-foreground)', letterSpacing: '-0.02em' }}
            >
              Opportunities
            </h1>
            <p className="font-caption text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
              Track deal values in ₹, probability, and pipeline stage progression
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              iconSize={14}
              onClick={async () => {
                setIsExporting(true);
                try {
                  await reportsApi.exportDataset('deals');
                } catch {
                  setExportError('Export failed. Please try again.');
                } finally {
                  setIsExporting(false);
                }
              }}
              loading={isExporting}
            >
              Export
            </Button>
            <Button 
              variant="default" 
              size="sm" 
              iconName="Plus" 
              iconPosition="left" 
              iconSize={14}
              onClick={() => {
                setSelectedOpportunity(null);
                setIsFormOpen(true);
              }}
            >
              Add Opportunity
            </Button>
          </div>
        </div>

        {exportError && (
          <div className="mb-4 rounded-lg border px-4 py-3" style={{ borderColor: 'var(--color-error)', background: 'var(--color-card)' }}>
            <p className="font-caption text-sm" style={{ color: 'var(--color-error)' }}>{exportError}</p>
          </div>
        )}

        {loadError && (
          <div className="mb-4 rounded-lg border px-4 py-3 flex items-center justify-between gap-4" style={{ borderColor: 'var(--color-error)', background: 'var(--color-card)' }}>
            <p className="font-caption text-sm" style={{ color: 'var(--color-error)' }}>{loadError}</p>
            <Button
              variant="outline"
              size="sm"
              iconName="RefreshCw"
              iconPosition="left"
              iconSize={14}
              onClick={async () => {
                setIsLoading(true);
                setLoadError('');
                try {
                  const data = await loadOpportunitiesData();
                  setStats(data.statsResponse);
                  setOpportunities(data.opportunitiesResponse);
                  setStageConfig(data.configResponse);
                } catch (error) {
                  setLoadError(error?.message || 'Unable to load opportunities.');
                } finally {
                  setIsLoading(false);
                }
              }}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Stats */}
        <div className="mb-6">
          <OpportunitiesStats stats={statsCards} />
        </div>

        {/* Search + Stage filter */}
        <div className="flex flex-col gap-3 mb-5">
          <div className="relative w-full">
            <Icon
              name="Search"
              size={16}
              color="var(--color-text-secondary)"
              className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"
            />
            <input
              type="text"
              placeholder="Search by opportunity name, company, rep..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e?.target?.value)}
              className="w-full pl-9 pr-4 py-2.5 rounded-lg border font-caption text-sm transition-colors"
              style={{
                borderColor: 'var(--color-border)',
                background: 'var(--color-card)',
                color: 'var(--color-foreground)',
                outline: 'none',
              }}
              onFocus={(e) => (e.target.style.borderColor = 'var(--color-primary)')}
              onBlur={(e) => (e.target.style.borderColor = 'var(--color-border)')}
            />
            {searchQuery && (
              <button
                className="absolute right-3 top-1/2 -translate-y-1/2"
                onClick={() => setSearchQuery('')}
                style={{ color: 'var(--color-text-secondary)' }}
              >
                <Icon name="X" size={14} color="currentColor" />
              </button>
            )}
          </div>

          {/* Stage filter pills - scrollable on mobile */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1" style={{ WebkitOverflowScrolling: 'touch' }}>
            {stageOptions?.map((s) => (
              <button
                key={s?.value}
                onClick={() => setStageFilter(s?.value)}
                className="flex-shrink-0 px-3 py-2 rounded-lg border font-caption text-xs font-medium transition-all duration-150"
                style={{
                  background: stageFilter === s?.value ? 'var(--color-primary)' : 'var(--color-card)',
                  color: stageFilter === s?.value ? '#fff' : 'var(--color-foreground)',
                  borderColor: stageFilter === s?.value ? 'var(--color-primary)' : 'var(--color-border)',
                  whiteSpace: 'nowrap',
                }}
              >
                {s?.label}
              </button>
            ))}
          </div>
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="rounded-xl border px-4 py-16 text-center" style={{ borderColor: 'var(--color-border)', background: 'var(--color-card)' }}>
            <p className="font-caption text-sm" style={{ color: 'var(--color-text-secondary)' }}>Loading opportunities...</p>
          </div>
        ) : (
        <OpportunitiesTable 
          searchQuery={searchQuery} 
          stageFilter={stageFilter}
          stages={stageConfig?.stages}
          isFormOpen={isFormOpen}
          setIsFormOpen={setIsFormOpen}
          selectedOpportunity={selectedOpportunity}
          setSelectedOpportunity={setSelectedOpportunity}
          initialOpportunities={opportunities}
          onSubmitOpportunity={handleOpportunitySubmit}
          onDeleteOpportunity={handleOpportunityDelete}
          canDelete={isManager}
          onAddNew={() => {
            setSelectedOpportunity(null);
            setIsFormOpen(true);
          }}
        />
        )}

        {/* Footer */}
        <div
          className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 mt-4 border-t"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <p className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>
            &copy; {new Date()?.getFullYear()} CRMAnalytics. All rights reserved.
          </p>
          <div className="flex items-center gap-1.5">
            <Icon name="Shield" size={12} color="var(--color-success)" />
            <span className="font-caption text-xs" style={{ color: 'var(--color-text-secondary)' }}>SSL Secured</span>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
